import {
  CheckCircle2,
  Pause,
  Phone,
  PhoneIncoming,
  PhoneOff,
  PhoneOutgoing,
} from "lucide-react";
import { useCallback } from "react";
import { useTranslations } from "use-intl";
import { CallActionButton } from "@/components/communications/call-action-button";
import { formatCallStatus } from "@/lib/format-duration";
import { useAllProperties } from "@/lib/hooks/use-all-properties";
import { useCallCounterparty } from "@/lib/hooks/use-call-counterparty";
import { ONE_SECOND_MS, useNow } from "@/lib/hooks/use-now";
import { colorForProperty } from "@/lib/property-color";
import { propertyShortLabel } from "@/lib/property-short-code";
import { type ActiveCall, useVoice } from "@/lib/twilio/voice-context";
import { cn } from "@/lib/utils";

/** The call the host is on while another rings. */
export interface CallWaiting {
  /** The answers that hold or end it, offered only where answering holds it. */
  choices?: WaitingChoices;
  /** The call already on hold behind the live one, if any. */
  heldCall: ActiveCall | null;
  liveCall: ActiveCall;
  onHangUp: (callSid: string) => void;
}

interface WaitingChoices {
  /** False while another call is already held: CallKit keeps two at most. */
  canHold: boolean;
  onEndAndAccept: () => void;
}

/** Whom a call is with, as the call screens name them, or undefined. */
export function useCallPartyName(call: ActiveCall): string | undefined {
  const { name, number } = useCallCounterparty(call);
  return name ?? call.displayName ?? number ?? undefined;
}

/**
 * For a host with several properties, which one a call rang for: its colour,
 * and its name, or its short code where room is tight. Nothing for one, or
 * for a call that names none (one the host placed).
 */
export function CallPropertyTag({
  className,
  compact,
  propertyId,
}: {
  className?: string;
  compact?: boolean;
  propertyId: string | null | undefined;
}) {
  const properties = useAllProperties();
  const property = properties.find((p) => p.id === propertyId);
  if (!(property && properties.length > 1)) {
    return null;
  }
  return (
    <span
      className={cn(
        "inline-flex max-w-full min-w-0 shrink-0 items-center gap-1.5",
        className
      )}
    >
      <span
        aria-hidden="true"
        className="size-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: colorForProperty(property) }}
      />
      <span className="truncate">
        {compact ? propertyShortLabel(property) : property.name}
      </span>
    </span>
  );
}

/** Atop the ringing screen: the call the host is on now. */
export function OnCallStrip({
  call,
  heldCall,
  onHangUp,
}: {
  call: ActiveCall;
  heldCall: ActiveCall | null;
  onHangUp: (callSid: string) => void;
}) {
  const t = useTranslations("communications.callWaiting");
  const tCall = useTranslations("communications.activeCallView");
  const tLabels = useTranslations("labels");
  const now = useNow(ONE_SECOND_MS, Boolean(call.startedAt));
  const name = useCallPartyName(call);
  const { callSid } = call;
  const handleHangUp = useCallback(
    () => onHangUp(callSid),
    [callSid, onHangUp]
  );
  return (
    <div className="mb-4 flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 shadow-sm">
      <Phone aria-hidden="true" className="size-4 shrink-0 text-success" />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-foreground">
          {name ? t("onCallWith", { name }) : t("onCall")}
        </span>
        <span className="flex items-center gap-2 text-xs text-muted-foreground tabular-nums">
          {formatCallStatus(tLabels, call, now)}
          <CallPropertyTag compact propertyId={call.caller?.propertyId} />
        </span>
        {heldCall ? <HeldCallLine call={heldCall} /> : null}
      </div>
      <button
        aria-label={tCall("hangUp")}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm transition-transform active:scale-95"
        onClick={handleHangUp}
        type="button"
      >
        <PhoneOff className="size-4" />
      </button>
    </div>
  );
}

/** Atop the call screen: the call on hold behind it, and the way back to it. */
function HeldCallStrip({
  call,
  onSwap,
}: {
  call: ActiveCall;
  onSwap: () => void;
}) {
  const t = useTranslations("communications.callWaiting");
  const tLabels = useTranslations("labels");
  const name = useCallPartyName(call);
  return (
    <div
      className="flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 shadow-sm"
      data-testid="held-call-strip"
    >
      <Pause aria-hidden="true" className="size-4 shrink-0 text-amber-500" />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-foreground">
          {name ?? t("callOnHold")}
        </span>
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          {tLabels("callStatus.onHold")}
          <CallPropertyTag compact propertyId={call.caller?.propertyId} />
        </span>
      </div>
      <button
        className="flex h-9 items-center gap-1.5 rounded-full border border-border bg-background px-4 text-sm font-medium text-foreground transition-transform hover:bg-muted active:scale-95"
        onClick={onSwap}
        type="button"
      >
        <PhoneOutgoing aria-hidden="true" className="size-4" />
        {t("swap")}
      </button>
    </div>
  );
}

/** Atop the call screen: a call that just ended, and the call on hold. */
export function CallWaitingHeader() {
  const { endedCall, heldCall, swapCalls } = useVoice();
  const handleSwap = useCallback(() => {
    void swapCalls();
  }, [swapCalls]);
  return (
    <div className="flex flex-col gap-3">
      {endedCall ? (
        <EndedCallLine
          call={endedCall}
          className="justify-center text-sm [&_svg]:size-4"
        />
      ) : null}
      {heldCall ? <HeldCallStrip call={heldCall} onSwap={handleSwap} /> : null}
    </div>
  );
}

/** For a few seconds after a call ends while another stays: which one ended. */
export function EndedCallLine({
  call,
  className,
}: {
  call: ActiveCall;
  className?: string;
}) {
  const t = useTranslations("communications.callWaiting");
  const name = useCallPartyName(call);
  return (
    <span
      className={cn(
        "flex max-w-full items-center gap-1.5 text-xs text-muted-foreground",
        className
      )}
      role="status"
    >
      <CheckCircle2 aria-hidden="true" className="size-3" />
      <span className="min-w-0 truncate">
        {name ? t("callEndedWith", { name }) : t("callEnded")}
      </span>
    </span>
  );
}

/** In the in-call bar: the call on hold behind the one it shows, a tap away. */
export function HeldCallSwap({ call }: { call: ActiveCall }) {
  const t = useTranslations("communications.callWaiting");
  const name = useCallPartyName(call);
  const { swapCalls } = useVoice();
  const handleSwap = useCallback(() => {
    void swapCalls();
  }, [swapCalls]);
  const label = name ? t("onHoldWith", { name }) : t("callOnHold");
  return (
    <button
      aria-label={`${t("swap")}: ${label}`}
      className="flex min-h-10 max-w-full items-center gap-2 text-xs text-muted-foreground"
      onClick={handleSwap}
      type="button"
    >
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-amber-500 text-amber-500 transition-transform active:scale-95">
        <Pause aria-hidden="true" className="size-3.5" />
      </span>
      <span className="min-w-0 truncate">{name ?? label}</span>
    </button>
  );
}

/** One line on the ringing screen: the call on hold behind the live one. */
export function HeldCallLine({ call }: { call: ActiveCall }) {
  const t = useTranslations("communications.callWaiting");
  const name = useCallPartyName(call);
  return (
    <span className="flex max-w-full items-center gap-1.5 text-xs text-muted-foreground">
      <Pause aria-hidden="true" className="size-3 shrink-0 text-amber-500" />
      <CallPropertyTag compact propertyId={call.caller?.propertyId} />
      <span className="min-w-0 truncate">
        {name ? t("onHoldWith", { name }) : t("callOnHold")}
      </span>
    </span>
  );
}

/**
 * The ringing screen's answers. During a call: Decline, End & Accept, and
 * Hold & Accept while nothing else is held.
 */
export function IncomingCallActions({
  onAccept,
  onReject,
  testIdPrefix,
  choices,
}: {
  choices?: WaitingChoices;
  onAccept: () => void;
  onReject: () => void;
  testIdPrefix: string;
}) {
  const t = useTranslations("communications.callWaiting");
  return (
    <div
      className={cn(
        "flex items-start gap-4",
        choices ? "justify-between" : "justify-around"
      )}
    >
      <CallActionButton
        icon={<PhoneOff className="size-6" />}
        label={t("decline")}
        onClick={onReject}
        testId={`${testIdPrefix}-decline`}
        variant="destructive"
      />
      {choices ? (
        <CallActionButton
          icon={<PhoneIncoming className="size-6" />}
          label={t("endAndAccept")}
          onClick={choices.onEndAndAccept}
          testId={`${testIdPrefix}-end-and-accept`}
          variant="success"
        />
      ) : null}
      {choices?.canHold === false ? null : (
        <CallActionButton
          icon={
            choices ? (
              <Pause className="size-6" />
            ) : (
              <Phone className="size-6" />
            )
          }
          label={choices ? t("holdAndAccept") : t("accept")}
          onClick={onAccept}
          testId={`${testIdPrefix}-accept`}
          variant="success"
        />
      )}
    </div>
  );
}
